import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Activity } from 'lucide-react';

export default function SocialFeed({ compact = false }) {
  const [feed, setFeed] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    api.get('/feed/symbols').then(r => setSymbols(r.data.symbols)).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const url = filter === 'ALL' ? `/feed?limit=${compact ? 8 : 20}` : `/feed?symbol=${filter}&limit=${compact ? 8 : 20}`;
        const r = await api.get(url);
        if (alive) setFeed(r.data.feed);
      } catch (_e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [compact, filter]);

  const timeAgo = (iso) => {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const chipCls = (active) =>
    `px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
      active
        ? 'bg-[#FCD535] border-[#FCD535] text-[#0B0E11]'
        : 'bg-transparent border-[#2B3139] text-[#848E9C] hover:text-white hover:border-[#FCD535]/40'
    }`;

  return (
    <div className="border border-[#2B3139] bg-[#181A20]" data-testid="social-feed">
      <div className="px-4 py-3 border-b border-[#2B3139] flex flex-wrap items-center justify-between gap-2">
        <div className="text-white font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#FCD535]" />
          Live Trades
        </div>
        <div className="flex flex-wrap gap-1.5 items-center" data-testid="feed-filters">
          <button onClick={() => setFilter('ALL')} className={chipCls(filter === 'ALL')} data-testid="filter-all">All</button>
          {symbols.slice(0, 8).map(s => (
            <button key={s} onClick={() => setFilter(s)} className={chipCls(filter === s)} data-testid={`filter-${s}`}>{s}</button>
          ))}
        </div>
      </div>
      {feed.length === 0 ? (
        <div className="p-6 text-center text-[#848E9C] text-sm">
          No trades{filter !== 'ALL' ? ` for ${filter}` : ''} yet.
        </div>
      ) : feed.map(f => (
        <div key={f.trade_id} className="grid grid-cols-12 items-center px-4 py-2.5 border-b border-[#2B3139]/60 hover:bg-[#2B3139]/50 text-sm">
          <div className="col-span-5 flex items-center gap-2">
            {f.picture ? <img src={f.picture} className="w-6 h-6 rounded-full" /> : (
              <div className="w-6 h-6 rounded-full bg-[#2B3139] grid place-items-center text-white text-xs font-semibold">{f.name?.[0]?.toUpperCase()}</div>
            )}
            <span className="text-white truncate">{f.name}</span>
          </div>
          <div className="col-span-4">
            <span className={`uppercase text-xs font-semibold ${f.side === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{f.side}ed</span>
            <span className="text-[#EAECEF] font-mono ml-1">{f.amount.toFixed(4)}</span>
            <Link to={`/trade/${f.symbol.toLowerCase()}`} className="text-[#FCD535] font-mono ml-1 hover:underline">{f.symbol}</Link>
          </div>
          <div className="col-span-2 text-right font-mono text-[#B7BDC6] text-xs">${f.total_usd.toFixed(0)}</div>
          <div className="col-span-1 text-right text-[#5E6673] text-xs">{timeAgo(f.created_at)}</div>
        </div>
      ))}
    </div>
  );
}
